import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import SpeedGauge from "@/components/SpeedGauge";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Anchor,
  Activity,
  Settings,
  FileText,
  BarChart3,
  Cloud,
  Monitor,
  Server,
  CheckCircle,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ihelmPlatform from "@/assets/iHelm_header_picture.png";
import ihelmDashboard from "@/assets/cloud-dashboard.png";
import ihelmRealtime from "@/assets/ihelm-realtime.png";
import ihelmEnginePerformance from "@/assets/ihelm-engine-performance.png";
import ihelmEmissionReport from "@/assets/ihelm-emission-report.png";
import cetaaiDashboard from "@/assets/cetaai-dashboard.png";
import digitalTwinBranded from "@/assets/digital-twin-clean.png";
import speedOptimization from "@/assets/speed-optimization.png";
import vesselMonitoring from "@/assets/vessel-monitoring.png";
import ihelmOnVessel from "@/assets/ihelm-on-vessel.png";
import captainDisplay from "@/assets/captain-display.png";

const IHelm = () => {
  
  const { t, i18n } = useTranslation('ihelm');

  const differentiators = [
    { title: t('decisionSupport.diff1Title'), description: t('decisionSupport.diff1Desc') },
    { title: t('decisionSupport.diff2Title'), description: t('decisionSupport.diff2Desc') },
    { title: t('decisionSupport.diff3Title'), description: t('decisionSupport.diff3Desc') },
  ];

  const modules = [
    { icon: Anchor, title: t('features.vms.title'), description: t('features.vms.description'), benefit: t('features.vms.benefit'), image: "vesselMonitoring" },
    { icon: Activity, title: t('features.motion.title'), description: t('features.motion.description'), benefit: t('features.motion.benefit') },
    { icon: Settings, title: t('features.engine.title'), description: t('features.engine.description'), benefit: t('features.engine.benefit'), image: "engineMonitoring" },
    { icon: FileText, title: t('features.reporting.title'), description: t('features.reporting.description'), benefit: t('features.reporting.benefit'), image: "emissionReport" },
    { icon: BarChart3, title: t('features.speed.title'), description: t('features.speed.description'), benefit: t('features.speed.benefit'), image: "speedOptimization" },
  ];

  const systemComponents = [
    { icon: Cloud, title: t('system.cloud'), description: t('system.cloudDesc') },
    { icon: Monitor, title: t('system.captain'), description: t('system.captainDesc') },
    { icon: Server, title: t('system.cmu'), description: t('system.cmuDesc') },
  ];

  const faqs = Array.from({ length: 9 }, (_, i) => ({
    question: t(`faq.q${i + 1}`),
    answer: t(`faq.a${i + 1}`),
  }));

  return (
    <div className="min-h-screen section-fade">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.cetasol.com/ihelm-intelligent-platform" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cetasol.com/ihelm-intelligent-platform" />
        <meta property="og:image" content="https://www.cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://www.cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "iHelm", "item": "https://www.cetasol.com/ihelm-intelligent-platform" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "iHelm",
          "description": t('meta.description'),
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "provider": { "@type": "Organization", "name": "Cetasol" },
          "url": "https://www.cetasol.com/ihelm-intelligent-platform"
        })}</script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="container mx-auto px-6 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="md:col-span-3"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-3">{t('hero.label')}</p>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2 leading-tight">
                iHelm
              </h1>
              <p className="text-lg text-foreground mb-6">{t('hero.subtitle')}</p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t('hero.description')}
              </p>
              <div className="flex items-center gap-8">
                <div>
                  <span className="font-display text-4xl font-bold text-foreground">{t('hero.stat1Value')}</span>
                  <p className="text-sm text-foreground mt-2"><span className="font-bold">{t('hero.stat1Label')}</span><br />{t('hero.stat1Detail')}</p>
                </div>
                <div className="w-px h-16 bg-border shrink-0" />
                <div>
                  <span className="font-display text-4xl font-bold text-foreground">{t('hero.stat2Value')}</span>
                  <p className="text-sm text-foreground mt-2"><span className="font-bold">{t('hero.stat2Label')}</span><br />{t('hero.stat2Detail')}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="md:col-span-4 flex items-center justify-start overflow-visible"
            >
              <img
                src={ihelmPlatform}
                alt="iHelm Platform"
                className="w-[120%] h-auto max-w-none object-contain"
              />
            </motion.div>
          </div>
        </section>

        {/* Onboard Captain Display */}
        <section className="bg-surface-light py-16 mb-20">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
              >
              <img
                  src={captainDisplay}
                  alt="iHelm captain display showing real-time speed, fuel rate, and CO2 emissions on the bridge"
                  className="w-full "
                  loading="lazy"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">{t('captainDisplay.label')}</p>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                  {t('captainDisplay.heading')}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  {t('captainDisplay.p1')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {t('captainDisplay.p2')}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Online Cloud Dashboard */}
        <section className="container mx-auto px-6 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('cloudDashboard.label')}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('cloudDashboard.heading')}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                {t('cloudDashboard.p1')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('cloudDashboard.p2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <img
                src={ihelmDashboard}
                alt="iHelm cloud dashboard showing fleet overview with vessel tracking and performance analytics"
                className="w-full "
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        <div className="h-4" />

        {/* Decision-support */}
        <section className="bg-surface-light py-16 mb-20">
          <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('decisionSupport.heading')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              {t('decisionSupport.description')}
            </p>
            <ul className="space-y-3 text-left max-w-2xl mx-auto">
              {differentiators.map((d, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="font-display font-semibold text-foreground text-base">{d.title}</span>
                    <span className="text-muted-foreground text-base"> {d.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-6 mb-20">
          <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-12 text-center">{t('features.label')}</p>

          <div className="space-y-20 max-w-5xl mx-auto">
            {modules.map((mod, i) => {
              const isReversed = i % 2 !== 0;
              const featureImages: Record<string, string> = { speedOptimization, vesselMonitoring, engineMonitoring: ihelmEnginePerformance, emissionReport: ihelmEmissionReport };
              const imgSrc = mod.image ? featureImages[mod.image] : null;
              const hasImage = !!imgSrc;
              return (
                <div key={mod.title} className={`${hasImage ? `grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${isReversed ? 'md:[direction:rtl]' : ''}` : 'max-w-2xl'}`}>
                  {hasImage && (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.7 }}
                      className="md:[direction:ltr]"
                    >
                      <img
                        src={imgSrc}
                        alt={`${mod.title} feature`}
                        className="w-full "
                        loading="lazy"
                      />
                    </motion.div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: hasImage ? 0.15 : 0 }}
                    className="md:[direction:ltr]"
                  >
                    <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-1">{mod.title}</h3>
                    <p className="text-sm text-foreground/80 font-medium mb-3">{mod.benefit}</p>
                    <p className="text-muted-foreground leading-relaxed">{mod.description}</p>
                  </motion.div>
                </div>
            );
            })}
          </div>
        </section>

        <section className="bg-surface-light py-32 md:py-40 mb-20">
          <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
              {t('segmentLinks.heading')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto text-center">
              {t('segmentLinks.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: "Ferry", to: "/segments/ferry" },
                { label: "Tug", to: "/segments/tug" },
                { label: "Merchant", to: "/segments/merchant" },
                { label: "CTV", to: "/segments/ctv" },
                { label: "Pilot", to: "/segments/pilot" },
                { label: "Fishing", to: "/segments/fishing" },
              ].map((s) => (
                <Link
                  key={s.label}
                  to={s.to}
                  className="px-5 py-2.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-secondary/18 transition-colors"
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </motion.div>
          </div>
        </section>

        <section className="container mx-auto px-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="mt-24 mb-16 text-center"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('underTheHood.label')}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('underTheHood.heading')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('underTheHood.description')}
            </p>
          </motion.div>

          {/* CetaAI section */}
          <div id="cetaai" className="scroll-mt-32 grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                {t('cetaai.heading')}
              </h2>
              <p className="text-sm text-foreground/80 font-medium mb-4">{t('cetaai.subtitle')}</p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('cetaai.p1')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('cetaai.p2')}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <img
                src={cetaaiDashboard}
                alt="CetaAI - Actionable intelligence from your fleet data"
                className="w-full"
                loading="lazy"
              />
            </motion.div>
          </div>

          {/* Digital Twin */}
          <div id="digital-twin" className="scroll-mt-32 grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="order-2 md:order-1"
            >
              <img
                src={digitalTwinBranded}
                alt="Digital Twin Modelling - A live operational model built from real vessel data"
                className="w-full"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="order-1 md:order-2"
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                {t('digitalTwin.heading')}
              </h2>
              <p className="text-sm text-foreground/80 font-medium mb-4">{t('digitalTwin.subtitle')}</p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t('digitalTwin.p1')}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t('digitalTwin.p2')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Compliance */}
        <section className="bg-surface-light py-16 mb-20">
          <div className="container mx-auto px-6 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4 text-center">{t('compliance.label')}</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4 text-center">
                {t('compliance.heading')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t('compliance.description')}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:divide-x md:divide-border">
                {[
                  { label: "CII", desc: t('compliance.cii') },
                  { label: "EEXI", desc: t('compliance.eexi') },
                  { label: "EU MRV", desc: t('compliance.eumrv') },
                  { label: "IMO DCS", desc: t('compliance.imodcs') },
                ].map((item, i) => (
                  <div key={item.label} className={i > 0 ? "md:pl-8" : ""}>
                    <span className="text-sm font-display font-bold text-foreground">{item.label}</span>
                    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CetaFuel cross-sell */}
        <section className="container mx-auto px-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t('cetafuelCrossSell.heading')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('cetafuelCrossSell.description')}
            </p>
            <Link
              to="/cetafuel"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors"
            >
              {t('cetafuelCrossSell.cta')} <ArrowRight size={16} />
            </Link>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-6 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-5xl mx-auto border border-border p-8 md:p-10"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8">
              {t('faq.heading')}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display font-normal text-sm md:text-base text-foreground">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default IHelm;
